import React, { useRef, useState, useEffect } from 'react';
import { Pen, Eraser, RotateCcw, Check, X } from 'lucide-react';

const SignaturePad = ({ onSave, onCancel, initialSignature = null, label = "Draw Your Signature" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [context, setContext] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    setContext(ctx);

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2; // Higher resolution
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Set drawing styles
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setIsEmpty(false);
      };
      img.src = initialSignature;
    }
  }, [initialSignature]);

  const startDrawing = (e) => {
    if (!context) return;
    
    setIsDrawing(true);
    setIsEmpty(false);

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;

    e.preventDefault(); // Prevent scrolling on mobile

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!context) return;
    setIsDrawing(false);
    context.closePath();
  };

  const clearCanvas = () => {
    if (!context || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    context.clearRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (isEmpty) {
      alert('Please draw your signature first');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onSave(signatureData);
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '8px',
      padding: '1.5rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: 600 }}>
        {label}
      </h3>

      {/* Canvas */}
      <div style={{
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        marginBottom: '1rem',
        background: '#fff',
        position: 'relative'
      }}>
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            width: '100%',
            height: '200px',
            cursor: 'crosshair',
            touchAction: 'none',
            display: 'block'
          }}
        />
        
        {/* Signature line */}
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: '20px',
          right: '20px',
          borderBottom: '2px dashed #d1d5db',
          pointerEvents: 'none'
        }} />

        {/* Helper text */}
        {isEmpty && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#9ca3af',
            fontSize: '0.875rem',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Pen size={18} />
            <span>Draw your signature here</span>
          </div>
        )}
      </div>

      {/* Instructions */}
      <p style={{
        fontSize: '0.75rem',
        color: '#6b7280',
        marginBottom: '1rem',
        textAlign: 'center'
      }}>
        Draw using your mouse or touch screen. Sign on the line above.
      </p>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <button
          onClick={clearCanvas}
          disabled={isEmpty}
          style={{
            padding: '0.625rem 1rem',
            background: isEmpty ? '#f3f4f6' : '#fff',
            color: isEmpty ? '#9ca3af' : '#6b7280',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontWeight: 500,
            cursor: isEmpty ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <RotateCcw size={16} />
          Clear
        </button>

        <button
          onClick={handleSave}
          disabled={isEmpty}
          style={{
            padding: '0.625rem 1.25rem',
            background: isEmpty ? '#e5e7eb' : 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 600,
            cursor: isEmpty ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <Check size={16} />
          Save Signature
        </button>

        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              padding: '0.625rem 1rem',
              background: '#fff',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem'
            }}
          >
            <X size={16} />
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default SignaturePad;
